import React, { useEffect, useState } from "react";
import "./App.css";
import PlantDisplay from "./PlantDisplay";
import SensorHistoryGraph from "./SensorHistoryGraph";

function App() {
  const [latest, setLatest] = useState({ time: null, data: [null, null, null] });
  const [tooWet, setTooWet] = useState(300);
  const [tooDry, setTooDry] = useState(1700);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [allData, setAllData] = useState([]);
  const [timeframe, setTimeframe] = useState("day");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [viewMode, setViewMode] = useState("box"); // "box" or "table"
  const [showRawData, setShowRawData] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL;

  // Helper to get ISO string for midnight of a date in UTC
  function getISODateString(date) {
    // Always use UTC for backend queries
    const d = new Date(date);
    return d.toISOString().slice(0, 19);
  }

  // Conversion: raw sensor value to percent (1100->75%, 1700->55%)
  function convertRawValue(value) {
    if (value == null) return null;
    const v = Math.max(Math.min(Number(value), percentToRaw(0)), percentToRaw(100));
    return Math.round(75 - ((v - 1100) / (1700 - 1100)) * 20);
  }

  // Conversion: percent to raw sensor value (inverse of above)
  function percentToRaw(percent) {
    // 75% -> 1100, 55% -> 1700
    return Math.round(1100 + ((75 - percent) / 20) * (1700 - 1100));
  }

  // Fetch sensor data only once on mount and when showRawData changes
  useEffect(() => {
      fetch(`${backend}/latest`)
        .then((res) => res.json())
        .then((data) => {
          setLatest(data);
          setLoading(false);
        });
  }, [backend]);

  // Fetch thresholds and update showRawData together
  const handleRawDataToggle = (checked) => {
    fetch(`${backend}/thresholds`)
      .then((res) => res.json())
      .then((data) => {
        setTooWet(data.too_wet);
        setTooDry(data.too_dry);
        setShowRawData(checked);
      });
  };

  // Fetch all data for the graph with timeframe
  useEffect(() => {
    let url = `${backend}/data`;
    let from;
    const now = new Date();
    if (timeframe === "month") {
      from = new Date(now);
      from.setMonth(now.getMonth() - 1);
      url += `?from=${encodeURIComponent(getISODateString(from))}`;
    } else if (timeframe === "week") {
      from = new Date(now);
      from.setDate(now.getDate() - 7);
      url += `?from=${encodeURIComponent(getISODateString(from))}`;
    } else if (timeframe === "day") {
      from = new Date(now);
      from.setDate(now.getDate() - 1);
      url += `?from=${encodeURIComponent(getISODateString(from))}`;
    } else if (timeframe === "custom") {
      if (customFrom && customTo) {
        const fromIso = `${customFrom}T00:00:00`;
        const toIso = `${customTo}T23:59:59`;
        url += `?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`;
      } else {
        setAllData([]); // Clear data if custom range is incomplete
        return;
      }
    }
    fetch(url)
      .then((res) => res.json())
      .then((data) => setAllData(data));
  }, [backend, timeframe, customFrom, customTo]);

  const saveThresholds = () => {
    fetch(`${backend}/thresholds`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ too_wet: tooWet, too_dry: tooDry }),
    })
      .then((res) => {
        if (res.ok) {
          setShowSettings(false);
        } else {
          alert("Failed to save thresholds");
        }
      })
      .catch(() => alert("Error saving thresholds"));
  };

  // Prepare data for display: convert if not raw
  const displayLatest = showRawData
    ? latest
    : {
        ...latest,
        data: Array.isArray(latest.data)
          ? latest.data.map(convertRawValue)
          : [],
      };

  const displayTooWet = showRawData ? tooWet : convertRawValue(tooWet);
  const displayTooDry = showRawData ? tooDry : convertRawValue(tooDry);

  const displayAllData = showRawData
    ? allData
    : allData.map((entry) => ({
        ...entry,
        data: Array.isArray(entry.data)
          ? entry.data.map(convertRawValue)
          : [],
      }));

  // Clamp helpers for thresholds
  const minTooWet = percentToRaw(100);
  const maxTooDry = percentToRaw(0);

  // Handlers to enforce constraints
  const handleTooWetChange = (value) => {
    let v = Math.max(minTooWet, Math.min(value, tooDry - 1));
    setTooWet(v);
  };

  const handleTooDryChange = (value) => {
    let v = Math.min(maxTooDry, Math.max(value, tooWet + 1));
    setTooDry(v);
  };

  // Fetch and show thresholds when opening settings
  const openSettings = () => {
    fetch(`${backend}/thresholds`)
      .then((res) => res.json())
      .then((data) => {
        setTooWet(data.too_wet);
        setTooDry(data.too_dry);
        setShowSettings(true);
      });
  };

  return (
    <div className="container">
      <button
        className="settingsButton"
        onClick={openSettings}
        aria-label="Open settings"
      >
        ⚙️
      </button>

      <h1>🌿 Plant Monitor</h1>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <PlantDisplay
          viewMode={viewMode}
          data={Array.isArray(displayLatest.data) ? displayLatest.data : []}
          tooWet={displayTooWet}
          tooDry={displayTooDry}
          raw={showRawData}
        />
      )}
      {/* View mode iOS-style toggle */}
      <div className="viewModeToggle">
        <span className="viewModeLabel">Table View</span>
        <label className="ios-switch">
          <input
            type="checkbox"
            checked={viewMode === "table"}
            onChange={e => setViewMode(e.target.checked ? "table" : "box")}
            aria-label="Toggle table/box view"
          />
          <span className="ios-slider"></span>
        </label>
      </div>
      {/* Toggle for raw data */}
      <div className="viewModeToggle">
        <span className="viewModeLabel">Raw Data</span>
        <label className="ios-switch">
          <input
            type="checkbox"
            checked={showRawData}
            onChange={e => handleRawDataToggle(e.target.checked)}
            aria-label="Toggle raw/converted data"
          />
          <span className="ios-slider"></span>
        </label>
      </div>
      {/* Interactive graph for sensor values over time */}
      <div className="historyGraphContainer">
        <h2>History</h2>
        <div style={{ marginBottom: 10 }}>
          <label>
            &nbsp;
            <select
              value={timeframe}
              onChange={e => setTimeframe(e.target.value)}
              className="timeframeDropdown"
              style={{ fontSize: "1em" }}
            >
              <option value="day">Last Day</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="all">All</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          {timeframe === "custom" && (
            <span style={{ marginLeft: 10 }}>
              From:{" "}
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                style={{ fontSize: "1em" }}
              />
              &nbsp;To:{" "}
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                style={{ fontSize: "1em" }}
              />
            </span>
          )}
        </div>
        <div className="historyGraphInner">
          <SensorHistoryGraph
            allData={displayAllData}
            timeframe={timeframe}
            customFrom={customFrom}
            customTo={customTo}
            raw={showRawData}
          />
        </div>
      </div>

      {showSettings && (
        <div
          className="modalOverlay"
          onClick={() => setShowSettings(false)}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="modalContent"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Settings</h2>
            <div style={{ marginBottom: 12, color: "#666", fontSize: "0.97em" }}>
              <b>Current thresholds:</b>
              <br />
              Too Wet: {tooWet} ({convertRawValue(tooWet)}%)<br />
              Too Dry: {tooDry} ({convertRawValue(tooDry)}%)
            </div>
            <label>
              Too Wet if&nbsp;
              {"<"}
              &nbsp;
              <input
                type="number"
                value={tooWet}
                min={minTooWet}
                max={tooDry - 1}
                step={1}
                onChange={(e) => handleTooWetChange(Number(e.target.value))}
                className="input"
              />
              &nbsp;
              <span style={{ color: "#888", fontSize: "0.95em" }}>
                ({convertRawValue(tooWet)}%)
              </span>
            </label>
            <br />
            <label>
              Too Dry if&nbsp;
              {">"}
              &nbsp;
              <input
                type="number"
                value={tooDry}
                min={tooWet + 1}
                max={maxTooDry}
                step={1}
                onChange={(e) => handleTooDryChange(Number(e.target.value))}
                className="input"
              />
              &nbsp;
              <span style={{ color: "#888", fontSize: "0.95em" }}>
                ({convertRawValue(tooDry)}%)
              </span>
            </label>

            <div className="modalButtons">
              <button
                onClick={saveThresholds}
                className="saveButton"
                aria-label="Save settings"
              >
                Save
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="cancelButton"
                aria-label="Cancel settings"
              >
                Cancel
              </button>
            </div>
            {/* Timestamp display moved here */}
            <div style={{ marginTop: 16, fontSize: "0.95em", color: "#666" }}>
              Last sensor update:&nbsp;
              {latest.time
                ? (() => {
                    const date = new Date(latest.time);
                    const pad = (n) => n.toString().padStart(2, "0");
                    const yy = date.getFullYear().toString().slice(2);
                    const mm = pad(date.getMonth() + 1);
                    const dd = pad(date.getDate());
                    const hh = pad(date.getHours());
                    const min = pad(date.getMinutes());
                    return (
                      <>
                        <span>{dd}.{mm}.{yy} {hh}:{min} Uhr</span>
                      </>
                    );
                  })()
                : "-"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
